/**
 * RoutineResults — Summary after completing a daily routine.
 * Shows per-exercise breakdown, total stats, and share button.
 */

interface ExerciseResult {
  exercise: string;
  reps: number;
}

interface RoutineResultsProps {
  routineName: string;
  exercises: ExerciseResult[];
  totalReps: number;
  totalTimeSeconds: number;
  onRunAgain: () => void;
  onShare?: () => void;
  onClose: () => void;
}

export default function RoutineResults({
  routineName,
  exercises,
  totalReps,
  totalTimeSeconds,
  onRunAgain,
  onShare,
  onClose,
}: RoutineResultsProps) {
  const minutes = Math.floor(totalTimeSeconds / 60);
  const seconds = totalTimeSeconds % 60;
  const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

  return (
    <div className="mx-auto max-w-md space-y-6 py-4">
      {/* Header */}
      <div className="text-center">
        <div className="text-4xl mb-2">🏆</div>
        <h2 className="text-xl font-bold text-gray-900">Routine Complete!</h2>
        <p className="text-sm text-gray-500">{routineName}</p>
      </div>

      {/* Aggregate stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-blue-50 p-3 text-center">
          <p className="text-2xl font-black text-blue-600 tabular-nums">{totalReps}</p>
          <p className="text-[10px] font-medium text-blue-400">Total Reps</p>
        </div>
        <div className="rounded-xl bg-green-50 p-3 text-center">
          <p className="text-2xl font-black text-green-600">{exercises.length}</p>
          <p className="text-[10px] font-medium text-green-400">Exercises</p>
        </div>
        <div className="rounded-xl bg-purple-50 p-3 text-center">
          <p className="text-2xl font-black text-purple-600 tabular-nums">{timeStr}</p>
          <p className="text-[10px] font-medium text-purple-400">Time</p>
        </div>
      </div>

      {/* Per-exercise breakdown */}
      <div className="rounded-2xl border border-gray-200 divide-y divide-gray-100">
        {exercises.map((ex, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-500">
                {i + 1}
              </span>
              <span className="text-sm font-medium text-gray-900">{ex.exercise}</span>
            </div>
            <span className="text-lg font-bold text-gray-900 tabular-nums">{ex.reps}</span>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2">
        {onShare && (
          <button
            type="button"
            onClick={onShare}
            className="w-full rounded-xl bg-blue-600 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-700"
          >
            Share to The Combine
          </button>
        )}
        <button
          type="button"
          onClick={onRunAgain}
          className="w-full rounded-xl bg-gray-100 py-3 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-200"
        >
          Run Again
        </button>
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-xl py-3 text-sm font-medium text-gray-400 transition-colors hover:text-gray-600"
        >
          Done
        </button>
      </div>
    </div>
  );
}
